import { supabase } from '../lib/supabase';

const PETTY_CASH_ACCOUNT_CODE = '1105';

// Petty Cash Service for managing petty cash transactions
export const pettyCashService = {
  // Get petty cash account details
  async getPettyCashAccount(organizationId) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      const { data, error } = await supabase
        ?.from('chart_of_accounts')
        ?.select('id, account_code, account_name, current_balance, account_type, account_subtype')
        ?.eq('organization_id', organizationId)
        ?.eq('account_code', PETTY_CASH_ACCOUNT_CODE)
        ?.single();

      if (error) {
        throw new Error(`Failed to fetch petty cash account: ${error?.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error fetching petty cash account:', error);
      throw error;
    }
  },

  // Get petty cash transactions (journal entries related to petty cash account)
  async getPettyCashTransactions(organizationId, options = {}) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      // First get the petty cash account ID
      const pettyCashAccount = await this.getPettyCashAccount(organizationId);
      
      let query = supabase
        ?.from('journal_entry_lines')
        ?.select(`
          id,
          debit_amount,
          credit_amount,
          description,
          reference,
          line_order,
          created_at,
          journal_entry:journal_entries(
            id,
            entry_number,
            entry_date,
            description,
            status,
            notes,
            reference_number,
            created_by,
            created_at
          )
        `)
        ?.eq('account_id', pettyCashAccount?.id)
        ?.order('created_at', { ascending: false });

      // Apply optional filters
      if (options?.startDate) {
        query = query?.gte('journal_entry.entry_date', options?.startDate);
      }
      if (options?.endDate) {
        query = query?.lte('journal_entry.entry_date', options?.endDate);
      }
      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch petty cash transactions: ${error?.message}`);
      }

      // Transform data for easier consumption
      return data?.map(line => ({
        id: line?.id,
        journalEntryId: line?.journal_entry?.id,
        entryNumber: line?.journal_entry?.entry_number,
        entryDate: line?.journal_entry?.entry_date,
        description: line?.description || line?.journal_entry?.description,
        amount: line?.debit_amount > 0 ? -line?.debit_amount : line?.credit_amount, // Negative for expenses, positive for replenishments
        transactionType: line?.debit_amount > 0 ? 'expense' : 'replenishment',
        debitAmount: line?.debit_amount,
        creditAmount: line?.credit_amount,
        reference: line?.reference || line?.journal_entry?.reference_number,
        status: line?.journal_entry?.status,
        notes: line?.journal_entry?.notes,
        createdAt: line?.created_at,
        createdBy: line?.journal_entry?.created_by
      })) || [];
    } catch (error) {
      console.error('Error fetching petty cash transactions:', error);
      throw error;
    }
  },

  // Create a petty cash expense transaction
  async createPettyCashExpense(organizationId, userId, expenseData) {
    if (!organizationId || !userId) {
      throw new Error('Organization ID and User ID are required');
    }

    try {
      // Get petty cash account
      const pettyCashAccount = await this.getPettyCashAccount(organizationId);
      
      // Generate entry number (simplified - in production you'd use a sequence)
      const entryNumber = `PC-${Date.now()}`;
      
      const { data: journalEntry, error: journalError } = await supabase
        ?.from('journal_entries')
        ?.insert({
          organization_id: organizationId,
          entry_number: entryNumber,
          entry_date: expenseData?.date || new Date()?.toISOString()?.split('T')?.[0],
          description: `Petty Cash: ${expenseData?.description}`,
          notes: expenseData?.notes,
          reference_number: expenseData?.reference,
          status: 'posted',
          created_by: userId,
          total_debit: parseFloat(expenseData?.amount),
          total_credit: parseFloat(expenseData?.amount),
          is_balanced: true
        })
        ?.select()
        ?.single();

      if (journalError) {
        throw new Error(`Failed to create journal entry: ${journalError?.message}`);
      }

      // Create journal entry lines (double entry)
      const journalLines = [
        // Debit the expense account (or general expense if no specific account)
        {
          journal_entry_id: journalEntry?.id,
          account_id: expenseData?.expenseAccountId || pettyCashAccount?.id, // For now, we'll use petty cash account
          description: expenseData?.description,
          debit_amount: parseFloat(expenseData?.amount),
          credit_amount: 0,
          line_order: 1,
          reference: expenseData?.reference
        },
        // Credit the petty cash account
        {
          journal_entry_id: journalEntry?.id,
          account_id: pettyCashAccount?.id,
          description: expenseData?.description,
          debit_amount: 0,
          credit_amount: parseFloat(expenseData?.amount),
          line_order: 2,
          reference: expenseData?.reference
        }
      ];

      const { data: lines, error: linesError } = await supabase
        ?.from('journal_entry_lines')
        ?.insert(journalLines)
        ?.select();

      if (linesError) {
        throw new Error(`Failed to create journal entry lines: ${linesError?.message}`);
      }

      return {
        journalEntry,
        lines,
        success: true,
        message: 'Petty cash expense recorded successfully'
      };
    } catch (error) {
      console.error('Error creating petty cash expense:', error);
      throw error;
    }
  },

  // Create a petty cash replenishment transaction
  async createPettyCashReplenishment(organizationId, userId, replenishmentData) {
    if (!organizationId || !userId) {
      throw new Error('Organization ID and User ID are required');
    }

    try {
      // Get petty cash account
      const pettyCashAccount = await this.getPettyCashAccount(organizationId);
      
      // Generate entry number
      const entryNumber = `PCR-${Date.now()}`;
      
      const { data: journalEntry, error: journalError } = await supabase
        ?.from('journal_entries')
        ?.insert({
          organization_id: organizationId,
          entry_number: entryNumber,
          entry_date: replenishmentData?.date || new Date()?.toISOString()?.split('T')?.[0],
          description: `Petty Cash Replenishment: ${replenishmentData?.description}`,
          notes: replenishmentData?.notes,
          reference_number: replenishmentData?.reference,
          status: 'posted',
          created_by: userId,
          total_debit: parseFloat(replenishmentData?.amount),
          total_credit: parseFloat(replenishmentData?.amount),
          is_balanced: true
        })
        ?.select()
        ?.single();

      if (journalError) {
        throw new Error(`Failed to create journal entry: ${journalError?.message}`);
      }

      // Create journal entry lines (double entry)
      const journalLines = [
        // Debit the petty cash account
        {
          journal_entry_id: journalEntry?.id,
          account_id: pettyCashAccount?.id,
          description: replenishmentData?.description,
          debit_amount: parseFloat(replenishmentData?.amount),
          credit_amount: 0,
          line_order: 1,
          reference: replenishmentData?.reference
        },
        // Credit the funding account (bank account or cash account)
        {
          journal_entry_id: journalEntry?.id,
          account_id: replenishmentData?.fundingAccountId || pettyCashAccount?.id, // For now, default to petty cash
          description: replenishmentData?.description,
          debit_amount: 0,
          credit_amount: parseFloat(replenishmentData?.amount),
          line_order: 2,
          reference: replenishmentData?.reference
        }
      ];

      const { data: lines, error: linesError } = await supabase
        ?.from('journal_entry_lines')
        ?.insert(journalLines)
        ?.select();

      if (linesError) {
        throw new Error(`Failed to create journal entry lines: ${linesError?.message}`);
      }

      return {
        journalEntry,
        lines,
        success: true,
        message: 'Petty cash replenishment recorded successfully'
      };
    } catch (error) {
      console.error('Error creating petty cash replenishment:', error);
      throw error;
    }
  },

  // Get petty cash summary (balance, total expenses, recent activity)
  async getPettyCashSummary(organizationId, options = {}) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      const pettyCashAccount = await this.getPettyCashAccount(organizationId);
      const transactions = await this.getPettyCashTransactions(organizationId, { 
        limit: options?.transactionLimit || 10 
      });

      const currentDate = new Date();
      const currentMonth = currentDate?.getMonth() + 1;
      const currentYear = currentDate?.getFullYear();

      // Calculate monthly totals
      const monthlyTransactions = transactions?.filter(t => {
        const transactionDate = new Date(t?.entryDate);
        return transactionDate?.getMonth() + 1 === currentMonth && 
               transactionDate?.getFullYear() === currentYear;
      });

      const monthlyExpenses = monthlyTransactions
        ?.filter(t => t?.transactionType === 'expense')
        ?.reduce((sum, t) => sum + Math.abs(t?.amount), 0) || 0;

      const monthlyReplenishments = monthlyTransactions
        ?.filter(t => t?.transactionType === 'replenishment')
        ?.reduce((sum, t) => sum + t?.amount, 0) || 0;

      return {
        account: pettyCashAccount,
        currentBalance: pettyCashAccount?.current_balance || 0,
        monthlyExpenses,
        monthlyReplenishments,
        netMonthlyChange: monthlyReplenishments - monthlyExpenses,
        totalTransactions: transactions?.length || 0,
        recentTransactions: transactions?.slice(0, 5),
        lastTransaction: transactions?.[0] || null,
        summary: {
          accountId: pettyCashAccount?.id,
          accountName: pettyCashAccount?.account_name,
          accountCode: pettyCashAccount?.account_code,
          balance: pettyCashAccount?.current_balance || 0,
          currency: 'USD' // Default currency, could be configurable
        }
      };
    } catch (error) {
      console.error('Error fetching petty cash summary:', error);
      throw error;
    }
  },

  // Get available expense accounts for petty cash transactions
  async getExpenseAccounts(organizationId) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      const { data, error } = await supabase
        ?.from('chart_of_accounts')
        ?.select('id, account_code, account_name, account_subtype')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'expense')
        ?.eq('allows_posting', true)
        ?.order('account_code');

      if (error) {
        throw new Error(`Failed to fetch expense accounts: ${error?.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching expense accounts:', error);
      throw error;
    }
  },

  // Get available funding accounts (cash/bank accounts) for replenishments
  async getFundingAccounts(organizationId) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      const { data, error } = await supabase
        ?.from('chart_of_accounts')
        ?.select('id, account_code, account_name, account_subtype, current_balance')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'asset')
        ?.in('account_subtype', ['cash_and_equivalents', 'current_asset'])
        ?.eq('allows_posting', true)
        ?.order('account_code');

      if (error) {
        throw new Error(`Failed to fetch funding accounts: ${error?.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching funding accounts:', error);
      throw error;
    }
  }
};

export default pettyCashService;